import { FC } from 'react';
import { Box, Divider, Flex, Image, Stack } from '@chakra-ui/react';

import { formatAmount } from 'common/formatAmount';
import { getCardRequestAssetSrc } from 'common/paymentCards/utils';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import { formatDate } from 'helpers/date';

import {
  isPhysicalPaymentCardRequest,
  isVirtualBudgetCardRequest,
  Request,
} from '../../../../hooks/useRequests';
import locales from './locales';

interface SummaryProps {
  request: Request;
}

const getLocaleKey = (
  request: Request,
): 'budgetCard' | 'physicalCard' | 'virtualCard' => {
  if (isVirtualBudgetCardRequest(request)) {
    return 'budgetCard';
  }

  if (isPhysicalPaymentCardRequest(request)) {
    return 'physicalCard';
  }

  return 'virtualCard';
};

const Summary: FC<SummaryProps> = ({ request }) => {
  const localeKey = getLocaleKey(request);

  return (
    <Box>
      <Box paddingBottom="space-16">
        <Image
          alt=""
          role="presentation"
          src={getCardRequestAssetSrc(request)}
          width="3.375rem"
        />
      </Box>

      <Box paddingBottom="space-8">
        <Heading>{locales.titles[localeKey]}</Heading>
      </Box>

      <Stack divider={<Divider />} spacing="space-0">
        {request.requester ? (
          <Flex justifyContent="space-between" paddingY="space-16">
            <Text>{locales.summary.requester}</Text>
            <Text variant="primary">
              {request.requester.firstName} {request.requester.lastName}
            </Text>
          </Flex>
        ) : null}

        <Flex justifyContent="space-between" paddingY="space-16">
          <Text>{locales.summary.date}</Text>
          <Text variant="primary">
            {formatDate(+new Date(request.createdAt), 'P')}
          </Text>
        </Flex>
        {isVirtualBudgetCardRequest(request) ? (
          <Flex justifyContent="space-between" paddingY="space-16">
            <Text>{locales.summary.limitPaymentAll}</Text>
            <Text variant="primary">
              {formatAmount({
                currency: 'EUR',
                forcePlusSign: false,
                value:
                  request.virtualBudgetCardOrderRequesterPayload
                    .limitPaymentAll,
                withNoMinorUnitDigits: true,
              })}
            </Text>
          </Flex>
        ) : null}
        {isVirtualBudgetCardRequest(request) ? (
          <Flex justifyContent="space-between" paddingY="space-16">
            <Text>{locales.summary.description}</Text>
            <Text textAlign="right" variant="primary">
              {request.virtualBudgetCardOrderRequesterPayload.description}
            </Text>
          </Flex>
        ) : null}
      </Stack>

      <Box marginBottom="space-24" marginX="-space-16">
        <Divider />
      </Box>
    </Box>
  );
};

export default Summary;
